import { Theme } from "ipctg-inflow-types/InFlowThemes"
import {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useState,
} from "react"

import { LOCAL_STORAGE_UI_SCHEME_PREFERENCE_KEY } from "../localStorageKeys"
import { log } from "../logging"
import { localStore } from "../store"
import {
  AppliedScheme,
  DocumentThemeMode,
  ScannedArea,
  UIScheme,
} from "../types/local"

import { ThemeContext } from "./useTheme"

export function ThemeProvider(props: PropsWithChildren) {
  const [systemScheme, setSystemScheme] = useState<AppliedScheme>("light")
  /*
  Temp hiding to disable dark mode

  const [uiScheme, setUIScheme] = useState<UIScheme>(
    (localStore.getItem(
      LOCAL_STORAGE_UI_SCHEME_PREFERENCE_KEY
    ) as UIScheme | null) || "system"
  )
  */
  const [uiScheme, setUIScheme] = useState<UIScheme>("light")
  const [scannedArea, setScannedArea] = useState<ScannedArea>()
  const [documentThemeMode, setDocumentThemeMode] =
    useState<DocumentThemeMode>("root")

  useLayoutEffect(() => {
    const scheme = uiScheme === "system" ? systemScheme : uiScheme
    document.documentElement.setAttribute("data-ui-scheme", scheme)
    return () => {
      document.documentElement.removeAttribute("data-ui-scheme")
    }
  }, [uiScheme, systemScheme])

  useLayoutEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

    if (mediaQuery.matches) {
      setSystemScheme("dark")
    }

    mediaQuery.addEventListener("change", () =>
      setSystemScheme(mediaQuery.matches ? "dark" : "light")
    )

    return () => {
      mediaQuery.removeEventListener("change", () =>
        setSystemScheme(mediaQuery.matches ? "dark" : "light")
      )
    }
  }, [])

  useLayoutEffect(() => {
    const styleID = "white-label-styles"
    let styleElement = document.getElementById(styleID) as HTMLStyleElement
    if (!styleElement) {
      styleElement = document.createElement("style")
      styleElement.id = styleID
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = generateWhiteLabelStyles(scannedArea)

    function generateWhiteLabelStyles(scannedArea?: ScannedArea) {
      if (!scannedArea) return ""
      return `
        ${generateThemeStyles(scannedArea.theme.light, "light")}
        \n
        ${generateThemeStyles(scannedArea.theme.dark, "dark")}
      `

      function generateThemeStyles(theme: Theme, mode: AppliedScheme) {
        const variables = Object.entries(theme.hues).flatMap(
          ([hue, swatches]) =>
            Object.entries(swatches).map(
              ([swatch, color]) => `--${hue}-${swatch}: ${color};`
            )
        )
        return `html[data-ui-scheme="${mode}"][data-use-root-theme="false"], [data-ui-scheme="${mode}"] *[data-use-root-theme="false"] {
          ${variables.join("\n      ")}
        }\n`
      }
    }
  }, [scannedArea])

  useLayoutEffect(() => {
    document.documentElement.setAttribute(
      "data-use-root-theme",
      documentThemeMode === "root" ? "true" : "false"
    )
    return () => {
      document.documentElement.removeAttribute("data-use-root-theme")
    }
  }, [documentThemeMode])

  const handleDocumentThemeModeChange = useCallback(
    (mode: DocumentThemeMode) => {
      log({
        level: "info",
        scope: "info",
        flow: "misc",
        message: `Document theme mode changed to: ${mode}`,
      })
      setDocumentThemeMode(mode)
    },
    []
  )

  return (
    <ThemeContext.Provider
      value={{
        uiScheme: uiScheme === "system" ? systemScheme : uiScheme,
        setUIScheme: handleUISchemeChange,
        scannedArea,
        setScannedArea,
        documentThemeMode,
        setDocumentThemeMode: handleDocumentThemeModeChange,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  )

  function handleUISchemeChange(uiScheme: AppliedScheme) {
    const newScheme = systemScheme === uiScheme ? "system" : uiScheme
    localStore.setItem(LOCAL_STORAGE_UI_SCHEME_PREFERENCE_KEY, newScheme)
    setUIScheme(newScheme)
  }
}
